import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { WeeklyHours, SpecialHours } from '@byte-store-control/shared';

// Store hours as returned from API (occasions format)
export interface StoreOccasion {
  occasion: string;
  isEnabled: boolean;
  hours?: Array<{
    timeFrame: {
      startDate?: string | null;
      endDate?: string | null;
      intervalStartTime: string;
      duration: string;
      days: number[];
      weight?: number;
      blackout?: boolean;
    };
    offsetOpen?: number;
    offsetClose?: number;
  }>;
}

// Bulk operation data types
export interface BulkHoursData {
  regularHours?: WeeklyHours;
  specialHours?: SpecialHours[];
}

export interface BulkChannelsData {
  channels: Array<{
    channel: 'UBEREATS' | 'DOORDASH' | 'GRUBHUB';
    enabled: boolean;
  }>;
}

export interface BulkItemsData {
  itemIds: string[];
  action: 'snooze' | 'unsnooze';
  duration?: number; // minutes
}

export type BulkOperationData = BulkHoursData | BulkChannelsData | BulkItemsData;

// Delivery aggregator interface
export interface DeliveryAggregator {
  id: string;
  name: string;
  displayName: string;
  enabled: boolean;
  icon?: string;
}

// Store data interface
export interface Store {
  storeNumber: string;
  storeName: string;
  name?: string; // Alias for storeName, used by mobile views
  isActive?: boolean; // Whether store is active, used by mobile views
  brand?: string;
  franchiseGroup?: string;
  timezone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  onlineOrders?: boolean; // Master toggle for accepting online orders
  deliveryAggregators?: DeliveryAggregator[]; // Dynamic list from backend
  channels?: {
    channel: 'UBEREATS' | 'DOORDASH' | 'GRUBHUB';
    status: 'ONLINE' | 'ACTIVE' | 'PAUSED' | 'DEACTIVATED';
    deliveryProvider: 'MARKETPLACE' | 'INTERNAL';
    externalStoreId?: string;
  }[]; // All ByteConnect channels with their status
  activeChannels?: {
    channel: 'UBEREATS' | 'DOORDASH' | 'GRUBHUB';
    status: 'ONLINE' | 'ACTIVE' | 'PAUSED' | 'DEACTIVATED';
    deliveryProvider: 'MARKETPLACE' | 'INTERNAL';
    externalStoreId?: string;
  }[]; // Deprecated - use 'channels' instead
  status?: 'active' | 'inactive' | 'error'; // Store operational status (not online order status)
  lastModified?: {
    timestamp: string;
    action: string;
    userName: string;
  };
  unavailableItems?: string[]; // Snoozed items
  hours?: StoreOccasion[]; // Store hours (occasions format from API)
  phone?: string;
  manager?: string;
}

// Filter options
export interface StoreFilters {
  searchTerm: string;
  brands: string[];
  franchiseGroups: string[];
  onlineOrders?: boolean; // Filter by online order status
  channels: {
    delivery?: boolean;
    pickup?: boolean;
    dineIn?: boolean;
  };
  status: ('active' | 'inactive' | 'error')[]; // Keep for legacy, but not actively used
  hasChanges?: boolean;
}

// Saved filter preset
export interface FilterPreset {
  id: string;
  name: string;
  filters: Partial<StoreFilters>;
  isDefault?: boolean;
}

// Bulk operation template
export interface BulkOperationTemplate {
  id: string;
  name: string;
  description?: string;
  operationType: 'store-hours' | 'channels' | 'items';
  data: BulkOperationData;
  createdAt: Date;
  usedCount: number;
}

// Context state interface - UI state only, stores come from useStoresQuery
interface StoreManagementContextState {
  // Selection state
  selectedStoreIds: Set<string>;
  filters: StoreFilters;
  filterPresets: FilterPreset[];
  bulkOperationTemplates: BulkOperationTemplate[];

  // Mobile state - persists selected store across mobile pages
  selectedMobileStore: string | null;
  setSelectedMobileStore: (storeNumber: string | null) => void;

  // Bulk operation loading state
  isApplyingBulkOperation: boolean;

  // Success notification (persists across component remounts)
  successMessage: string | null;
  showSuccess: (message: string) => void;
  clearSuccess: () => void;

  // Selection actions
  selectStore: (storeId: string) => void;
  deselectStore: (storeId: string) => void;
  toggleStoreSelection: (storeId: string) => void;
  selectAllStores: (storeIds: string[]) => void;
  clearSelection: () => void;
  selectStoresByFilter: (storeIds: string[]) => void;

  // Filters
  updateFilters: (filters: Partial<StoreFilters>) => void;
  clearFilters: () => void;
  saveFilterPreset: (name: string, isDefault?: boolean) => void;
  loadFilterPreset: (presetId: string) => void;
  deleteFilterPreset: (presetId: string) => void;

  // Templates
  saveBulkOperationTemplate: (template: Omit<BulkOperationTemplate, 'id' | 'createdAt' | 'usedCount'>) => void;
  applyBulkOperationTemplate: (templateId: string, storeIds?: string[]) => Promise<void>;
  deleteBulkOperationTemplate: (templateId: string) => void;

  // Selection computed values
  hasSelection: boolean;
  selectionCount: number;
}

const StoreManagementContext = createContext<StoreManagementContextState | undefined>(undefined);

interface StoreManagementProviderProps {
  children: ReactNode;
}

export const StoreManagementProvider: React.FC<StoreManagementProviderProps> = ({ children }) => {
  // Selection state
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<StoreFilters>({
    searchTerm: '',
    brands: [],
    franchiseGroups: [],
    channels: {},
    status: [],
    hasChanges: undefined,
  });
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([
    {
      id: 'all-stores',
      name: 'All Stores',
      filters: {},
      isDefault: true,
    },
    {
      id: 'delivery-enabled',
      name: 'Delivery Enabled',
      filters: { channels: { delivery: true } },
    },
    {
      id: 'needs-attention',
      name: 'Needs Attention',
      filters: { status: ['error'] },
    },
  ]);
  const [bulkOperationTemplates, setBulkOperationTemplates] = useState<BulkOperationTemplate[]>([]);
  const [isApplyingBulkOperation, setIsApplyingBulkOperation] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Mobile state - persists selected store across mobile pages
  const [selectedMobileStore, setSelectedMobileStore] = useState<string | null>(null);

  // Show success notification
  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    // Auto-clear after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  }, []);

  // Clear success notification
  const clearSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  // Selection actions
  const selectStore = useCallback((storeId: string) => {
    setSelectedStoreIds(prev => new Set([...prev, storeId]));
  }, []);

  const deselectStore = useCallback((storeId: string) => {
    setSelectedStoreIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(storeId);
      return newSet;
    });
  }, []);

  const toggleStoreSelection = useCallback((storeId: string) => {
    setSelectedStoreIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeId)) {
        newSet.delete(storeId);
      } else {
        newSet.add(storeId);
      }
      return newSet;
    });
  }, []);

  // Select all stores - receives store IDs from caller (who has access to stores via useStoresQuery)
  const selectAllStores = useCallback((storeIds: string[]) => {
    setSelectedStoreIds(new Set(storeIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedStoreIds(new Set());
  }, []);

  // Select stores by IDs - receives filtered store IDs from caller
  const selectStoresByFilter = useCallback((storeIds: string[]) => {
    setSelectedStoreIds(new Set(storeIds));
  }, []);

  // Filter actions
  const updateFilters = useCallback((newFilters: Partial<StoreFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      searchTerm: '',
      brands: [],
      franchiseGroups: [],
      channels: {},
      status: [],
      hasChanges: undefined,
    });
  }, []);

  const saveFilterPreset = useCallback((name: string, isDefault = false) => {
    const newPreset: FilterPreset = {
      id: `preset-${Date.now()}`,
      name,
      filters: { ...filters },
      isDefault,
    };
    setFilterPresets(prev => {
      if (isDefault) {
        return [newPreset, ...prev.map(p => ({ ...p, isDefault: false }))];
      }
      return [...prev, newPreset];
    });
  }, [filters]);

  const loadFilterPreset = useCallback((presetId: string) => {
    const preset = filterPresets.find(p => p.id === presetId);
    if (preset) {
      setFilters({ ...filters, ...preset.filters });
    }
  }, [filterPresets, filters]);

  const deleteFilterPreset = useCallback((presetId: string) => {
    setFilterPresets(prev => prev.filter(p => p.id !== presetId));
  }, []);

  // Template actions
  const saveBulkOperationTemplate = useCallback((
    template: Omit<BulkOperationTemplate, 'id' | 'createdAt' | 'usedCount'>
  ) => {
    const newTemplate: BulkOperationTemplate = {
      ...template,
      id: `template-${Date.now()}`,
      createdAt: new Date(),
      usedCount: 0,
    };
    setBulkOperationTemplates(prev => [...prev, newTemplate]);
  }, []);

  const applyBulkOperationTemplate = useCallback(async (
    templateId: string,
    storeIds?: string[]
  ) => {
    const template = bulkOperationTemplates.find(t => t.id === templateId);
    if (!template) return;

    setIsApplyingBulkOperation(true);
    try {
      // Get target stores
      const targetStoreIds = storeIds || Array.from(selectedStoreIds);

      // TODO: Implement actual API calls based on template.operationType
      // This would involve calling the appropriate API endpoints
      // For now, just simulate the operation
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update template usage count
      setBulkOperationTemplates(prev =>
        prev.map(t =>
          t.id === templateId
            ? { ...t, usedCount: t.usedCount + 1 }
            : t
        )
      );
    } finally {
      setIsApplyingBulkOperation(false);
    }
  }, [bulkOperationTemplates, selectedStoreIds]);

  const deleteBulkOperationTemplate = useCallback((templateId: string) => {
    setBulkOperationTemplates(prev => prev.filter(t => t.id !== templateId));
  }, []);

  // Selection computed values
  const hasSelection = selectedStoreIds.size > 0;
  const selectionCount = selectedStoreIds.size;

  const value: StoreManagementContextState = {
    // Selection state
    selectedStoreIds,
    filters,
    filterPresets,
    bulkOperationTemplates,
    selectedMobileStore,
    setSelectedMobileStore,
    isApplyingBulkOperation,
    successMessage,
    showSuccess,
    clearSuccess,

    // Selection actions
    selectStore,
    deselectStore,
    toggleStoreSelection,
    selectAllStores,
    clearSelection,
    selectStoresByFilter,

    // Filter actions
    updateFilters,
    clearFilters,
    saveFilterPreset,
    loadFilterPreset,
    deleteFilterPreset,

    // Template actions
    saveBulkOperationTemplate,
    applyBulkOperationTemplate,
    deleteBulkOperationTemplate,

    // Selection computed values
    hasSelection,
    selectionCount,
  };

  return (
    <StoreManagementContext.Provider value={value}>
      {children}
    </StoreManagementContext.Provider>
  );
};

export const useStoreManagement = () => {
  const context = useContext(StoreManagementContext);
  if (!context) {
    throw new Error('useStoreManagement must be used within a StoreManagementProvider');
  }
  return context;
};

/**
 * Helper function to filter stores based on StoreFilters.
 * Use this with stores from useStoresQuery and filters from useStoreManagement.
 */
export function filterStores(stores: Store[], filters: StoreFilters): Store[] {
  return stores.filter(store => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      if (
        !store.storeNumber.toLowerCase().includes(searchLower) &&
        !store.storeName.toLowerCase().includes(searchLower) &&
        !store.address?.city?.toLowerCase().includes(searchLower)
      ) {
        return false;
      }
    }

    // Brand filter
    if (filters.brands.length > 0 && store.brand) {
      if (!filters.brands.includes(store.brand)) {
        return false;
      }
    }

    // Franchise group filter
    if (filters.franchiseGroups.length > 0 && store.franchiseGroup) {
      if (!filters.franchiseGroups.includes(store.franchiseGroup)) {
        return false;
      }
    }

    // Online Orders filter
    if (filters.onlineOrders !== undefined) {
      if (store.onlineOrders !== filters.onlineOrders) {
        return false;
      }
    }

    // Channel filters (legacy - can be removed when channels are fully migrated to aggregators)
    if (filters.channels.delivery !== undefined) {
      const hasDelivery = store.deliveryAggregators?.some(agg => agg.enabled) ?? false;
      if (hasDelivery !== filters.channels.delivery) {
        return false;
      }
    }
    if (filters.channels.pickup !== undefined) {
      const hasPickup = store.deliveryAggregators?.find(agg => agg.id === 'native')?.enabled ?? false;
      if (hasPickup !== filters.channels.pickup) {
        return false;
      }
    }

    // Status filter (legacy - not actively used)
    if (filters.status.length > 0 && store.status) {
      if (!filters.status.includes(store.status)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Helper function to get selected stores from a list.
 * Use this with stores from useStoresQuery and selectedStoreIds from useStoreManagement.
 */
export function getSelectedStores(stores: Store[], selectedStoreIds: Set<string>): Store[] {
  return stores.filter(store => selectedStoreIds.has(store.storeNumber));
}